import { get as getByPath } from 'lodash-es';
import { ICompanyDataModel } from '../data-models/company.data-model';
import {
  FORMULA_COMPANY_RECORD,
  FORMULA_DATA_PREFIX,
  FORMULA_ROW_RECORD,
  FormulaFieldEntity,
  formulaFieldPath,
  formulaFieldsForEntity,
  FormulaFieldType,
  IFormulaFieldDataModel,
} from '../data-models/formula-field.data-model';
import { COMPANY_VIEW_TYPE } from '../data-models/view-config.data-model';
import {
  AstNode,
  EvalContext,
  evaluate,
  FormulaError,
  FormulaValue,
  getReferencedFields,
  isFormulaError,
  ListItem,
  parse,
} from '../formula-engine';
import { ColumnMeta, createColumnMeta } from '../types';
import { StandardFormatterId } from './formatter-service';
import { entityFieldTypes } from './formula-vocabulary';

/** Evaluated formula results live on the metrics row under this object, keyed by field key. */
export { FORMULA_DATA_PREFIX, formulaFieldPath, formulaFieldsForEntity };

/** Offset keeps injected column ids clear of real /column row ids in id-keyed maps. */
const FORMULA_COLUMN_ID_OFFSET = 9_000_000;

/** The formatter a formula column falls back to when the field has none, per result type. */
export const DEFAULT_FORMULA_FORMAT: Record<FormulaFieldType, StandardFormatterId> = {
  number: 'numeric',
  string: 'string',
  boolean: 'yesNo',
  date: 'date',
};

/**
 * The entity's fields whose column should render: what the column-injection
 * sites use, while evaluation and vocabularies keep the unfiltered entity set —
 * an unregistered field still materializes its value so visible formulas can
 * reference it.
 *
 * `displayedIds` are the fieldDisplayConfig registrations (the same table the
 * KPIs use), so display config is not a property of the field itself.
 */
export function displayedFormulaFieldsForEntity(
  fields: IFormulaFieldDataModel[],
  entity: FormulaFieldEntity,
  displayedIds: ReadonlySet<number>
): IFormulaFieldDataModel[] {
  return formulaFieldsForEntity(fields, entity).filter((field) => displayedIds.has(field.id));
}

/**
 * Which entity's formula fields a view's grid renders — PORTFOLIO_OVERVIEW and
 * RETURN_FORECAST both render OverviewTable over the metrics rows that
 * applyFormulaFields materializes company values onto; ROUND_TRACKER
 * renders the investment rows carrying round values. RETURN_FORECAST_2
 * (its own column pipeline) never sees formulaData, so formula columns stay
 * out of its pickers. The security grids live outside COMPANY_VIEW_TYPE and
 * inject 'captableInvestment' fields at the colDef level instead.
 */
export function formulaFieldEntityForView(companyViewType: COMPANY_VIEW_TYPE): FormulaFieldEntity | null {
  switch (companyViewType) {
    case COMPANY_VIEW_TYPE.PORTFOLIO_OVERVIEW:
    case COMPANY_VIEW_TYPE.RETURN_FORECAST:
      return 'company';
    case COMPANY_VIEW_TYPE.ROUND_TRACKER:
      return 'round';
    default:
      return null;
  }
}

/**
 * Present a formula field to the grid as a plain path-backed number column.
 * No valueGetter: the value is materialized on the row by the metrics
 * calculation pass, so sort/filter/export all see a normal field.
 */
export function formulaFieldToColumnMeta(field: IFormulaFieldDataModel): ColumnMeta {
  return createColumnMeta({
    id: FORMULA_COLUMN_ID_OFFSET + field.id,
    name: formulaFieldPath(field),
    path: formulaFieldPath(field),
    displayName: field.displayName,
    description: field.description,
    type: field.type,
    formatting: field.format ?? DEFAULT_FORMULA_FORMAT[field.type],
    // The column picker's data-points list buckets by groupName — without one
    // these land in a nameless group and are effectively unfindable there.
    groupName: 'Formulas',
    groupOrder: 9999,
    groupPriority: field.id,
    visible: true,
    default: true,
    sortable: true,
    filterable: true,
    exportable: true,
    // Group rows stay blank for now: aggregating a per-row formula needs
    // aggregate-the-inputs semantics, not sum/avg of results.
    aggregation: false,
    groupable: false,
  });
}

/** "Gross MOIC (net of fees)" → "grossMoicNetOfFees" — key generation for new fields. */
export function displayNameToKey(displayName: string): string {
  const words = displayName
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/);
  if (!words.length) {
    return '';
  }
  return words
    .map((word, i) => {
      if (i === 0) {
        // A leading acronym lowercases whole ("FMV" → "fmv", not "fMV")
        return /^[A-Z0-9]+$/.test(word) ? word.toLowerCase() : word.charAt(0).toLowerCase() + word.slice(1);
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('');
}

/** The formula-field keys a canonical formula references (its "formulaData." refs); [] if it does not parse. */
function referencedFormulaKeys(formula: string): string[] {
  try {
    return getReferencedFields(parse(formula))
      .filter((ref) => ref.startsWith(`${FORMULA_DATA_PREFIX}.`))
      .map((ref) => ref.slice(FORMULA_DATA_PREFIX.length + 1));
  } catch {
    return [];
  }
}

/**
 * Would saving `candidate` (its formula in canonical form) close a reference
 * cycle through the stored formula fields? Returns the cycle as a display-name
 * chain from the candidate back to itself (["B", "A", "B"]), or null. Only a
 * cycle through the candidate can be created by its save — anything
 * pre-existing among other fields is the evaluator's reentrancy guard's
 * concern. Unparseable stored formulas contribute no edges (they evaluate to
 * blank anyway). The candidate's key matches an existing field on edit, and on
 * create is the key that will be generated — a recreated key can close a cycle
 * through formulas that referenced its deleted predecessor.
 */
export function findFormulaCycle(
  candidate: Pick<IFormulaFieldDataModel, 'key' | 'displayName' | 'formula'>,
  fields: IFormulaFieldDataModel[]
): string[] | null {
  const deps = new Map(fields.map((field) => [field.key, referencedFormulaKeys(field.formula)]));
  const names = new Map(fields.map((field) => [field.key, field.displayName]));
  deps.set(candidate.key, referencedFormulaKeys(candidate.formula)); // the version being saved
  names.set(candidate.key, candidate.displayName);

  const visited = new Set<string>([candidate.key]);
  const walk = (key: string, path: string[]): string[] | null => {
    for (const dep of deps.get(key) ?? []) {
      if (dep === candidate.key) {
        return [...path, dep];
      }
      if (visited.has(dep)) {
        continue;
      }
      visited.add(dep);
      const found = walk(dep, [...path, dep]);
      if (found) {
        return found;
      }
    }
    return null;
  };
  const cycle = walk(candidate.key, [candidate.key]);
  return cycle && cycle.map((key) => names.get(key) ?? key);
}

export type WithFormulaData<T> = T & { [FORMULA_DATA_PREFIX]?: Record<string, FormulaValue | null> };

/**
 * The record a canonical ref's head segment names. The formula's own entity
 * exposes its row as one record ("metrics.fmv" reads row.fmv on a company
 * formula); "company.*" reads the company record. A ref with any other head
 * is read off the row as-is — that is where the root-level imports live
 * ("kpiData.<id>.…"), and where a formula stored before records still points
 * ("[fmv]"). Another entity's record name is not a record here: the
 * vocabulary never offers one, so such a ref only reads blank.
 */
function splitRecordRef(path: string): { record: string; rest: string } | null {
  const dot = path.indexOf('.');
  return dot === -1 ? null : { record: path.slice(0, dot), rest: path.slice(dot + 1) };
}

/**
 * Evaluate every formula field against every row, materializing results under
 * `row.formulaData.<key>` so the grid (and any other row consumer) reads them
 * as plain fields. Rows are copied, not mutated; error values (e.g. #DIV/0!),
 * unparseable formulas and blank results all materialize as null → blank
 * cells. Idempotent: re-running after a regroup recompute just overwrites
 * formulaData.
 *
 * Formulas may reference each other ("formulaData." refs): a referenced
 * formula evaluates lazily on first use, memoized per row, so definition
 * order never matters and its error values propagate Excel-style.
 *
 * Rows don't carry company values (the grid resolves those via valueGetters),
 * so company.* refs need `getCompany`; without it they read as blank. The
 * row's own record ("metrics.", "round." or "security.") reads the row.
 *
 * `context` carries the surface's evaluation environment — pass the dataset's
 * own as-of date (the previous-quarter pass gets the previous quarter's date),
 * so ASOFDATE() always means "the date this row's data is as of". Without it,
 * ASOFDATE() falls back to today.
 */
export function applyFormulaFields<T extends object>(
  rows: T[],
  formulaFields: IFormulaFieldDataModel[],
  getCompany?: (row: T) => ICompanyDataModel | undefined,
  context?: EvalContext
): WithFormulaData<T>[] {
  if (!rows.length || !formulaFields.length) {
    return rows;
  }
  const compiled: { key: string; ast: AstNode; rowRecord: string; entity: FormulaFieldEntity }[] = [];
  for (const field of formulaFields) {
    try {
      compiled.push({
        key: field.key,
        ast: parse(field.formula),
        rowRecord: FORMULA_ROW_RECORD[field.entity ?? 'company'],
        entity: field.entity ?? 'company',
      });
    } catch {
      // A stored formula that no longer parses must not take the grid down.
    }
  }
  if (!compiled.length) {
    return rows;
  }
  const compiledByKey = new Map(compiled.map((entry) => [entry.key, entry]));
  const formulaRefPrefix = `${FORMULA_DATA_PREFIX}.`;
  // The declared type of every ref an entity's formulas can name, so the engine reads a
  // date field's ISO text as a date and a boolean field as TRUE/FALSE.
  const fieldTypes = new Map(
    [...new Set(formulaFields.map((field) => field.entity ?? 'company'))].map((entity) => [
      entity,
      entityFieldTypes(entity, formulaFields),
    ])
  );
  return rows.map((row) => {
    const cache = new Map<string, FormulaValue | FormulaError | null>();
    const evaluating = new Set<string>();
    // The row record of the formula being evaluated — restored after a nested
    // formula ref, though same-entity refs always share it.
    let rowRecord = '';
    function evaluateKey(key: string): FormulaValue | FormulaError | null {
      const target = compiledByKey.get(key);
      if (!target) {
        // Deleted or unparseable target: blank — never a stale row value.
        return null;
      }
      const cached = cache.get(key);
      if (cached !== undefined) {
        return cached;
      }
      if (evaluating.has(key)) {
        // Saves reject cycles, but stored data may predate that check; the
        // guard turns one into an error value instead of unbounded recursion.
        return new FormulaError('#ERROR!', `Circular reference through [${key}]`);
      }
      evaluating.add(key);
      const outerRowRecord = rowRecord;
      rowRecord = target.rowRecord;
      const result = evaluate(target.ast, resolveField, {
        ...context,
        fieldType: fieldTypes.get(target.entity),
      });
      rowRecord = outerRowRecord;
      evaluating.delete(key);
      cache.set(key, result);
      return result;
    }
    function resolveField(path: string): FormulaValue | ListItem[] | FormulaError | null {
      if (path.startsWith(formulaRefPrefix)) {
        return evaluateKey(path.slice(formulaRefPrefix.length));
      }
      const ref = splitRecordRef(path);
      const value: unknown =
        ref?.record === FORMULA_COMPANY_RECORD
          ? getByPath(getCompany?.(row), ref.rest)
          : ref?.record === rowRecord
            ? getByPath(row, ref.rest)
            : getByPath(row, path);
      if (Array.isArray(value)) {
        return value as ListItem[]; // a list field, for REDUCE
      }
      return typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean'
        ? value
        : null;
    }
    const formulaData: Record<string, FormulaValue | null> = {};
    for (const { key } of compiled) {
      const result = evaluateKey(key);
      formulaData[key] = result === null || isFormulaError(result) ? null : result;
    }
    return { ...row, [FORMULA_DATA_PREFIX]: formulaData };
  });
}
